import { WebMethodOptions, WebMethodResponseMessage, GetTextStatus } from "./WebMethod";
import { showAlert } from "./UI";
import { i18n } from "./I18n";
import * as I18nKeysShared from "./Locales/shared-map.json";

class CallManagerSingleton {
    public processMsToExpire: (ms: number) => void = $.noop;
    public onLoginUrlEmpty: () => void = $.noop;
    public onUnauthorized = function () { };

    //#region Singleton pattern
    private static _instance: CallManagerSingleton;

    private constructor() { }

    public static get instance(): CallManagerSingleton {
        if (!CallManagerSingleton._instance) {
            CallManagerSingleton._instance = new CallManagerSingleton();
        }
        return CallManagerSingleton._instance;
    }
    //#endregion

    private get webMethodURL(): string {
        const querystring = window.location.href.split("?")[0];
        return querystring.split("#")[0] + "/ExecuteWebMethod";
    }

    /**
     * Aanroepen van een webmethod.
     * @param webmethodname De naam van de webmethod
     * @param parameters De parameter die moeten worden meegegeven richting de webmethod
     * @param options Eventuele opties
     * @returns Een ajax-request (de aanroep van de webmethod)
     */
    public execute(webmethodname: string, parameters?: object | null, options?: WebMethodOptions): JQueryXHR {
        if (!webmethodname) {
            throw "Invalid webmethodname";
        }

        // De opties bepalen
        const defaultOptions: WebMethodOptions = {
            async: true,
            onConnectionError: undefined,
            timeout: 60000
        };
        const _options: WebMethodOptions = $.extend({}, defaultOptions, options);

        // De data bepalen tbv de ajax-request
        const dataObject = CallManagerSingleton.createDataObject(webmethodname, parameters);

        // De ajax-settings bepalen
        const ajaxSettings: JQueryAjaxSettings = {
            type: "POST",
            timeout: _options.timeout,
            data: JSON.stringify(dataObject),
            contentType: "application/json; charset=utf-8",
            dataType: "json",
            async: _options.async,
            beforeSend: (xhr) => {
                xhr.setRequestHeader("X-WebMethod", webmethodname);
            }
        };

        // Het ajax-request uitvoeren
        const ajax = $.ajax(this.webMethodURL, ajaxSettings);

        // Standaard donecallback
        ajax.done((msg: WebMethodResponseMessage) => {
            this.doneCallback(msg.d);
        });

        // Standaard failcallback
        ajax.fail((jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => {
            this.failCallback(jqXHR, textStatus, errorThrown, _options.onConnectionError);
        });

        // Het ajax-request-object retourneren
        return ajax;
    }

    /**
     * Standaard done-callback.
     * @param resultset De payload van de response 
     */
    private doneCallback(resultset: any): void {
        if (typeof (resultset.loginurl) === "string") {
            if (resultset.message !== undefined) {
                const text = i18n.t(I18nKeysShared.request.message.error.callback, { message: resultset.message });
                $.when(showAlert(text)).then(() => {
                    this.goToLogin(resultset.loginurl);
                });
            }
            else {
                this.goToLogin(resultset.loginurl);
            }
        } else {
            this.processMsToExpire(resultset.msToExpire);
        }
    }

    /**
     * Standaard fail-callback.
     * @param jqXHR Het ajax-request
     * @param textStatus De statustekst
     * @param errorThrown De foutmelding
     * @param onConnectionError De eventuele delegate die moet worden aangeroepen als het ajax-request faalt
     */
    private failCallback(jqXHR: JQueryXHR, textStatus: string, errorThrown: string, onConnectionError?: (message: string, jqXHR: JQueryXHR, textStatus: string, errorThrown: string) => void): void {
        ////eventuele localstorage acties e.d. eerst uitvoeren
        if (onConnectionError) {
            const message = GetTextStatus(textStatus, jqXHR.status);
            onConnectionError(message, jqXHR, textStatus, errorThrown);
        }

        if (jqXHR.status.toString() === "401" || (jqXHR.status.toString() === "200" && textStatus === "parsererror" && jqXHR.responseText.indexOf("<") > -1)) {
            this.onUnauthorized();
        }
    }

    /**
     * Afhandelen van een 'redirect' naar de loginpagina.
     * @param loginURL De eventuele url van de loginpagina
     */
    private goToLogin(loginURL: string) {
        if (loginURL === "") {
            this.onLoginUrlEmpty();
        }
        else if (window.document.location) {
            window.document.location.href = loginURL;
        }
    }

    /**
     * Een data-object creëren wat kan worden meegegeven aan het ajax-request.
     * @param webmethodname De webmethodnaam
     * @param parameters De parameters
     * @returns Een samengesteld data-object
     */
    private static createDataObject(webmethodname: string, parameters?: object | null): any {
        let _parameters = { method: webmethodname };

        //INFO: property 'time' uit de oude executeCall lijkt niet gebruikt te worden dus die niet meer geïmplementeerd.

        _parameters = $.extend(_parameters, parameters);

        return { parameters: _parameters };
    }
}

/**
* De instantie van de CallManager.
* NIET VOOR ALGEMEEN GEBRUIK BESCHIKBAAR (te gebruiken via imports (vanuit TS) of via SyntessTS.WebMethods.WebMethod (vanuit JS)).
*/
export const CallManager = CallManagerSingleton.instance;
